// Outline service config (Post a Project, Atrium Stage 7).
//
// Source of truth = the Rails backend, NOT the design prototype:
//   whitelist  app/controllers/api/v1/project_postings_controller.rb (outline_posting_params)
//   views      app/views/job_postings/steps/outline/_one.._four.html.erb
//   validation app/models/job_postings/job_postings/step_validations.rb (outline__one.._four)
//   cost       app/models/job_postings/outline_posting.rb (#cost)
//   parity     tasks/post-a-project/parity/enhance-text.md (OUTLINE)
//
// Restorations vs the design prototype:
//   - subtitle is optional
//   - Plot Outline price = OUTLINE_COST + PLOT_OUTLINE_COST = 300 + 120 = $420 (not $120)
//   - collaborative is a BOOLEAN radio (true/false), NOT strings collaborative/non_collaborative
//   - real AI consent step (mode "3")
//   - real trigger-warning composite

import {
  OUTLINE_PACKAGES,
  WRITING_STYLES,
  WRITING_GENRES,
  WRITING_NICHES,
} from "../options"

// string[] -> [{ value, label }] for select fields.
const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }))

const money = (n) => Math.round(n * 100) / 100

const PLOT_OUTLINE = "Plot Outline"

const isPlot = (v = {}) => v.package_type === PLOT_OUTLINE

// The two outline packages are PriceList-driven (outline_cost / plot_outline_cost
// + outline_cost), so the displayed card + rail price trace to the customer's
// server-resolved outlinePrices (Insider-aware; Phase-1 outlinePrices, ruling C2 /
// tuw-app:P-001), with the OUTLINE_PACKAGES constant as the no-live-PriceList
// fallback. Professional Outline -> professional; Plot Outline -> plot.
const outlinePriceFor = (pkg, ctx = {}) => {
  const key = pkg.value === PLOT_OUTLINE ? "plot" : "professional"
  return ctx.outlinePrices?.[key] ?? pkg.price
}

// Design option descriptions (service-config.js). Em dash converted per the
// no-em-dash rule.
const OUTLINE_DESCRIPTIONS = {
  "Professional Outline":
    "Collaborate with a professional outline writer to structure your non-fiction book, researched and composed to your vision.",
  "Plot Outline":
    "A fiction writer + developmental editor work with you to map out your story, ironing out the details to bring it to life.",
}

export const outline = {
  key: "outline",
  postingKey: "outline_posting",
  name: "Outline",

  steps: () => [
    // Step 1 — Outline type (outline __one).
    {
      id: "type",
      title: "Outline type",
      sub: "A strong outline sets up a strong manuscript.",
      fields: [
        {
          // Prices are the real server-resolved outline costs (Professional $300 /
          // Plot $420 = OUTLINE_COST + PLOT_OUTLINE_COST), not the design's stale
          // "$120" literal (ruling C2 / tuw-app:P-001). The value/price divergence
          // is a registered options exception; label + descriptions match the design.
          type: "radio",
          key: "package_type",
          required: true,
          options: (v = {}, ctx = {}) =>
            OUTLINE_PACKAGES.map((p) => ({
              value: p.value,
              label: p.label,
              price: `$${money(outlinePriceFor(p, ctx)).toFixed(2)}`,
              sub: OUTLINE_DESCRIPTIONS[p.value],
            })),
        },
      ],
    },

    // Step 2 — Project details (outline __two).
    {
      id: "details",
      title: "Project details",
      fields: [
        {
          type: "wordcount",
          key: "word_count",
          label: "Word count",
          required: true,
          min: 1000,
          step: 500,
          help: "This is the word count of the book, used as a reference for the outline writer. It is not the length of the outline itself.",
        },
        {
          type: "select",
          key: "writing_style",
          label: "Writing style",
          required: true,
          blankLabel: "Select writing style",
          options: asOptions(WRITING_STYLES),
          when: (v) => isPlot(v),
        },
        {
          type: "select",
          key: "genre",
          label: "Genre",
          required: true,
          blankLabel: "Select Genre",
          options: asOptions([...WRITING_GENRES].sort()),
          when: (v) => isPlot(v),
        },
        {
          type: "select",
          key: "niche",
          label: "Niche",
          required: true,
          blankLabel: "Select Niche/Genre",
          options: asOptions([...WRITING_NICHES].sort()),
          when: (v) => v.package_type && !isPlot(v),
        },
        { type: "text", key: "title", label: "Title", required: true, placeholder: "Working title" },
        { type: "text", key: "subtitle", label: "Subtitle", required: false },
        {
          type: "toggle",
          key: "nsfw",
          label: "Topic is not safe for work",
          detailsKey: "nsfw_details",
          detailsLabel: "Reason topic is not safe for work",
          detailsType: "textarea",
          detailsRequired: true,
        },
        {
          type: "triggerWarning",
          label: "This book contains sensitive or triggering content",
          help: "Flag anything the outline writer should be aware of before taking the project.",
        },
        {
          type: "file",
          key: "file",
          label: "Requirements file",
          required: false,
          accept: ".doc,.docx,.pdf,.zip",
          placeholder: "Upload a file",
        },
      ],
    },

    // Step 3 — Collaboration (outline __three). BOOLEAN values (true/false) — the
    // engine RadioGroup emits the string forms, which Rails coerces to booleans.
    // The value divergence (true/false vs the design's collaborative/non_collaborative)
    // is a registered options exception; labels + descriptions match the design.
    {
      id: "collaboration",
      title: "Collaboration",
      sub: "How hands-on do you want to be with your outline writer? Up to 2 revisions are included either way.",
      fields: [
        {
          type: "radio",
          key: "collaborative",
          required: true,
          options: [
            {
              value: "true",
              label: "Collaborative",
              sub: "Work hand-in-hand with your outline writer, leaving comments and feedback throughout until the outline meets your expectations.",
            },
            {
              value: "false",
              label: "Non-collaborative",
              sub: "Leave it to your outline writer to create the perfect outline, then review it at the end to ensure it meets your requirements.",
            },
          ],
        },
      ],
    },

    // Step 4 — AI consent (outline __four; mode "3").
    {
      id: "consent",
      title: "AI use in your project",
      sub: "Choose how much your outline writer may use AI. Please select one.",
      fields: [
        {
          type: "consent",
          key: "ai_consent",
          mode: "3",
          label: "AI use in your project",
          required: true,
        },
      ],
    },
  ],

  // Instant estimate for the PriceRail. Ruby outline #cost = package price (plus
  // the shared add-on, contributed separately):
  //   Professional Outline = OUTLINE_COST            = $300
  //   Plot Outline         = OUTLINE_COST + PLOT     = $420
  estimate: (values = {}, ctx = {}) => {
    const empty = { lines: [], total: 0, isRange: false, framing: "free-to-post" }

    const pkg = OUTLINE_PACKAGES.find((p) => p.value === values.package_type)
    if (!pkg) return empty

    const amount = money(outlinePriceFor(pkg, ctx))

    return {
      lines: [{ label: pkg.label, amount }],
      total: amount,
      isRange: false,
      framing: "free-to-post",
    }
  },

  // /post/price entry (camelCase; underscored server-side). cost_fields for
  // outline_posting = [package_type]. Null until a package is chosen.
  pricePayload: (values = {}) => {
    if (!values.package_type) return null

    return {
      postingType: "outline_posting",
      packageType: values.package_type,
    }
  },
}
